import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { PackageService } from '../../../services/package.service';
import { Observable } from 'rxjs';
import { SalesPackageGridCardComponent } from '../sales-package-grid-card/sales-package-grid-card.component';
import { CommonModule } from '@angular/common';
import { Package } from '../../../shared/types';

/**
 * Renders a responsive grid of sales packages, exposes an observable stream of packages from PackageService, and emits events when a package is selected.
 * @component
 * @selector app-sales-package-grid
 * @example
 *   <app-sales-package-grid [packages]="samplePackages" (packageSelected)="onPackageSelected($event)"></app-sales-package-grid>
 * @input {any[]} packages - Initial array of package objects to display; optional local input that can be used in combination with the internal packages$ observable.
 * @output {EventEmitter<any>} packageSelected - Emitted when a package card is selected; payload is the selected package object.
 *
 * Key methods:
 * - constructor(packageService: PackageService): Injects PackageService and assigns packages$ to the service's packages$ observable for template binding and reactive updates.
 * - onPackageSelected(packageData: any): Emits the packageSelected event with the provided package payload when a package is chosen by the user.
 *
 * Important lifecycle notes:
 * - The component relies on the PackageService.packages$ observable for live package data; there are no additional Angular lifecycle hooks implemented in this class, so subscription management is delegated to the service and template async bindings.
 */
@Component({
  selector: 'app-sales-package-grid',
  standalone: true,
  imports: [SalesPackageGridCardComponent, CommonModule],
  templateUrl: './sales-package-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sales-package-grid.component.scss',
})
export class SalesPackageGridComponent {
  @Input() packages: any[] = [];
  @Output() packageSelected = new EventEmitter<any>();
  packages$: Observable<Package[]>;

  constructor(private packageService: PackageService) {
    this.packages$ = this.packageService.packages$;
  }

  onPackageSelected(packageData: any): void {
    this.packageSelected.emit(packageData);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
